import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { BookLock, Pencil } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A', violet: '#6C4BB5' };
const fmtVal = v => Array.isArray(v) ? v.join(', ') : typeof v === 'boolean' ? (v ? 'Yes' : 'No') : typeof v === 'number' ? v.toLocaleString() : String(v ?? '—');
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-AU') : '—';

export const DeclarationRegisterCard = ({ api, entityId, isAdmin }) => {
  const [view, setView] = useState(null);
  const [error, setError] = useState('');
  const [correcting, setCorrecting] = useState(null);
  const [cValue, setCValue] = useState('');
  const [cReason, setCReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/register/declarations/${entityId}`)
    .then(r => setView(r.data))
    .catch(err => setError(err?.response?.data?.detail || 'Failed to load the declaration register')), [api, entityId]);
  useEffect(() => { load(); }, [load]);

  const submitCorrection = async (regId) => {
    setBusy(true);
    try {
      let value = cValue.trim();
      if (value === 'true') value = true;
      else if (value === 'false') value = false;
      else if (value !== '' && !isNaN(Number(value))) value = Number(value);
      await api.post(`/register/declarations/${regId}/correct`, { corrected_value: value, reason: cReason });
      toast.success('APP 13 correction recorded on the register');
      setCorrecting(null); setCValue(''); setCReason('');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Correction failed');
    } finally { setBusy(false); }
  };

  if (error) return <Card className="p-4 rounded-md" data-testid="declaration-register-card"><p className="text-xs" style={{ color: C.red }}>{error}</p></Card>;
  if (!view) return <Card className="p-4 rounded-md" data-testid="declaration-register-card"><p className="text-xs" style={{ color: C.slate }}>Loading register view…</p></Card>;

  return (
    <Card className="p-4 rounded-md space-y-4" style={{ border: '1px solid rgba(108,75,181,0.25)' }} data-testid="declaration-register-card">
      <div className="flex items-start gap-2">
        <BookLock className="w-4 h-4 mt-0.5 shrink-0" style={{ color: C.violet }} />
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Declaration Register — register-side view</p>
          <p className="text-[11px] mt-0.5" style={{ color: C.slate }} data-testid="decl-boundary-note">
            {view.boundary}. Delta retention {view.retention?.delta_retention_years} years; values kept for the life of the record with APP 13 correction rights. Every read of this view is access-logged.
          </p>
        </div>
      </div>

      {view.values.length === 0 ? (
        <p className="text-sm py-3 text-center" style={{ color: C.slate }} data-testid="decl-empty">
          No register-side declarations recorded for this client yet — they are captured at each new submission.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left" style={{ color: C.slate }}>
                <th className="py-1.5 pr-3 font-medium">Declared field</th>
                <th className="py-1.5 pr-3 font-medium">Value</th>
                <th className="py-1.5 pr-3 font-medium">Class</th>
                <th className="py-1.5 pr-3 font-medium">Last declared</th>
                <th className="py-1.5 pr-3 font-medium">Times</th>
                {isAdmin && <th className="py-1.5 font-medium">APP 13</th>}
              </tr>
            </thead>
            <tbody>
              {view.values.map(v => (
                <React.Fragment key={v.reg_id}>
                  <tr className="border-t" style={{ borderColor: 'rgba(136,150,168,0.1)' }} data-testid={`decl-row-${v.field}`}>
                    <td className="py-1.5 pr-3 capitalize" style={{ color: C.navy }}>{v.field.replace(/_/g, ' ')}</td>
                    <td className="py-1.5 pr-3 font-mono" style={{ color: C.navy }}>{fmtVal(v.value)}
                      {(v.corrections || []).length > 0 && (
                        <span className="ml-1.5 px-1.5 py-px rounded text-[9px] font-bold" style={{ background: 'rgba(108,75,181,0.12)', color: C.violet }} data-testid={`decl-corrected-chip-${v.field}`}>CORRECTED</span>
                      )}
                    </td>
                    <td className="py-1.5 pr-3" style={{ color: C.slate }}>{(v.insurance_class || '').replace(/_/g, ' ')}</td>
                    <td className="py-1.5 pr-3" style={{ color: C.slate }}>{fmtDate(v.declared_at)}</td>
                    <td className="py-1.5 pr-3" style={{ color: C.slate }}>{v.declaration_count}</td>
                    {isAdmin && (
                      <td className="py-1.5">
                        <button onClick={() => { setCorrecting(correcting === v.reg_id ? null : v.reg_id); setCValue(''); setCReason(''); }}
                          data-testid={`decl-correct-btn-${v.field}`}
                          className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium transition-colors hover:opacity-80"
                          style={{ background: 'rgba(108,75,181,0.08)', color: C.violet }}>
                          <Pencil className="w-2.5 h-2.5" /> Correct
                        </button>
                      </td>
                    )}
                  </tr>
                  {correcting === v.reg_id && (
                    <tr>
                      <td colSpan={6} className="py-2 px-2" style={{ background: 'rgba(108,75,181,0.04)' }}>
                        <div className="flex flex-wrap items-end gap-2">
                          <div>
                            <p className="text-[10px] mb-0.5" style={{ color: C.slate }}>Corrected value</p>
                            <input value={cValue} onChange={e => setCValue(e.target.value)} data-testid="decl-correct-value-input"
                              className="px-2 py-1 border border-input rounded-sm text-xs bg-white w-40" placeholder={fmtVal(v.value)} />
                          </div>
                          <div className="flex-1 min-w-[220px]">
                            <p className="text-[10px] mb-0.5" style={{ color: C.slate }}>Reason (APP 13 — min 10 characters)</p>
                            <input value={cReason} onChange={e => setCReason(e.target.value)} data-testid="decl-correct-reason-input"
                              className="px-2 py-1 border border-input rounded-sm text-xs bg-white w-full" placeholder="Why the recorded value is inaccurate, out of date or incomplete" />
                          </div>
                          <button onClick={() => submitCorrection(v.reg_id)} disabled={busy || cReason.trim().length < 10 || cValue.trim() === ''}
                            data-testid="decl-correct-submit-btn"
                            className="px-3 py-1 rounded-sm text-xs font-medium text-white disabled:opacity-40"
                            style={{ background: C.violet }}>
                            Record correction
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {view.deltas.length > 0 && (
        <div data-testid="decl-deltas">
          <p className="text-[11px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: C.slate }}>Declaration deltas ({view.deltas.length})</p>
          <div className="space-y-1">
            {view.deltas.slice(0, 20).map((d, i) => (
              <div key={d.delta_id} className="flex items-center gap-2 text-[11px]" data-testid={`decl-delta-row-${i}`}>
                <span className="px-1.5 py-px rounded text-[9px] font-bold shrink-0"
                  style={{ background: d.kind === 'app13_correction' ? 'rgba(108,75,181,0.12)' : 'rgba(0,180,216,0.12)', color: d.kind === 'app13_correction' ? C.violet : '#0E7490' }}>
                  {d.kind === 'app13_correction' ? 'APP 13' : 'DELTA'}
                </span>
                <span className="capitalize" style={{ color: C.navy }}>{d.field.replace(/_/g, ' ')}</span>
                <span className="font-mono" style={{ color: C.slate }}>{fmtVal(d.previous)} → {fmtVal(d.current)}</span>
                <span className="ml-auto shrink-0" style={{ color: C.slate }}>{fmtDate(d.declared_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};

export default DeclarationRegisterCard;
